'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Lock, PlayCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface Video { id: string; title: string; description: string; duration: string; youtubeId?: string | null }
interface Data {
  signedIn: boolean;
  rule: { minPosts: number; minDays: number };
  progress: { posts: number; days: number; unlocked: boolean } | null;
  videos: Video[];
}

export default function AkademijaClient() {
  const { session, loading } = useAuth() as { session: any; loading: boolean };
  const [data, setData] = useState<Data | null>(null);

  useEffect(() => {
    if (loading) return;
    let alive = true;
    const headers: Record<string, string> = {};
    if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
    fetch('/api/academy', { headers, credentials: 'include', cache: 'no-store' })
      .then((r) => r.json()).then((d) => { if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, [session?.access_token, loading]);

  const unlocked = data?.progress?.unlocked === true;

  return (
    <main className="max-w-3xl mx-auto px-4 py-8">
      <p className="text-[10px] font-semibold uppercase mb-2" style={{ color: 'var(--accent)', letterSpacing: '0.15em' }}>
        Akademija · tehnička analiza
      </p>
      <h1 className="font-heading text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
        Video lekcije iz tehničke analize
      </h1>
      <p className="text-sm leading-6 mb-6" style={{ color: 'var(--text-secondary)' }}>
        Lekcije su nagrada za aktivne članove. Objavi najmanje {data?.rule.minPosts ?? 10} analiza ili signala sa
        grafikom, u najmanje {data?.rule.minDays ?? 3} različita dana, i sve tri lekcije se otključavaju.
      </p>

      {data && !unlocked && <Gate data={data} />}

      <div className="space-y-4 mt-6">
        {(data?.videos ?? []).map((v, i) => (
          <div key={v.id} className="card-base overflow-hidden">
            {unlocked && v.youtubeId ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${v.youtubeId}?rel=0&modestbranding=1`}
                title={v.title}
                allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                className="w-full aspect-video bg-black"
              />
            ) : (
              <div className="w-full aspect-video flex items-center justify-center" style={{ background: 'var(--bg-sunken)' }}>
                <div className="flex flex-col items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                  {unlocked ? <PlayCircle size={36} /> : <Lock size={32} />}
                  <span className="text-xs">{unlocked ? 'Video uskoro' : 'Zaključano'}</span>
                </div>
              </div>
            )}
            <div className="px-5 py-4">
              <div className="text-[11px] font-bold uppercase mb-1" style={{ color: 'var(--text-muted)', letterSpacing: '0.1em' }}>
                Lekcija {i + 1}{v.duration ? ` · ${v.duration}` : ''}
              </div>
              <h2 className="font-heading text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{v.title}</h2>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{v.description}</p>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

function Gate({ data }: { data: Data }) {
  if (!data.signedIn) {
    return (
      <div className="card-base px-5 py-5">
        <div className="flex items-center gap-2 font-bold" style={{ color: 'var(--text-primary)' }}>
          <Lock size={16} /> Samo za registrovane članove
        </div>
        <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
          Registracija je besplatna. Posle toga objavljuj svoje analize i signale i lekcije se otključavaju.
        </p>
        <div className="flex gap-2 mt-4">
          <Link href="/sign-up-login-screen?next=/akademija" className="btn-primary px-4 py-2 text-sm">Registruj se</Link>
          <Link href="/sign-up-login-screen?next=/akademija" className="btn-ghost px-4 py-2 text-sm">Prijavi se</Link>
        </div>
      </div>
    );
  }

  const p = data.progress ?? { posts: 0, days: 0, unlocked: false };
  const { minPosts, minDays } = data.rule;
  const postsLeft = Math.max(0, minPosts - p.posts);
  const daysLeft = Math.max(0, minDays - p.days);
  const pct = Math.min(100, Math.round((Math.min(p.posts, minPosts) / minPosts) * 100));

  return (
    <div className="card-base px-5 py-5">
      <div className="flex items-center justify-between gap-3">
        <div className="font-bold" style={{ color: 'var(--text-primary)' }}>Tvoj napredak</div>
        <div className="font-mono-nums text-sm" style={{ color: 'var(--text-secondary)' }}>
          {Math.min(p.posts, minPosts)}/{minPosts} objava
        </div>
      </div>
      <div className="h-2 rounded-full mt-3 overflow-hidden" style={{ background: 'var(--bg-sunken)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
      </div>
      <ul className="text-sm mt-4 space-y-1.5" style={{ color: 'var(--text-secondary)' }}>
        <Req ok={postsLeft === 0} text={postsLeft === 0 ? `${minPosts} objava sa grafikom` : `Još ${postsLeft} ${objava(postsLeft)} sa grafikom`} />
        <Req ok={daysLeft === 0} text={daysLeft === 0 ? `Objave u ${minDays} različita dana` : `Objave u još ${daysLeft} ${daysLeft === 1 ? 'dan' : 'dana'} (imaš ${p.days}/${minDays})`} />
      </ul>
      <Link href="/home-feed?post=true" className="btn-primary inline-block px-4 py-2 text-sm mt-4">Objavi analizu</Link>
    </div>
  );
}

// 1 objava · 2–4 objave · 5+ objava (11–14 → objava)
function objava(n: number) {
  const d = n % 10, h = n % 100;
  if (d === 1 && h !== 11) return 'objava';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'objave';
  return 'objava';
}

function Req({ ok, text }: { ok: boolean; text: string }) {
  return (
    <li className="flex items-center gap-2">
      {ok ? <CheckCircle2 size={15} style={{ color: 'var(--win)' }} /> : <span className="w-[15px] h-[15px] rounded-full border inline-block" style={{ borderColor: 'var(--border)' }} />}
      <span style={{ color: ok ? 'var(--text-primary)' : undefined }}>{text}</span>
    </li>
  );
}
